import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeMoeda,
  CampoDeSelecao,
  CampoDeTexto,
  Modal,
  ValorFiscal,
  ValorPercentual,
} from '../../componentes/ui';
import { invalidarPedidos } from '../../componentes/PedidosDeFechamento';
import { useCarteiraDoAssessor } from '../../hooks/useParticipacao';
import { useEmpresaCorrente } from '../../hooks/useSessao';
import { requisicaoAutenticada } from '../../servicos/http';
import { lerValorDigitado } from '../../servicos/formato';
import { pedirFechamento } from '../../servicos/participacao';
import { ErroDaApi } from '../ErroDaApi';
import { Item } from './comum';
import type { SimulacaoPontual } from './tipos';

/**
 * O simulador do assessor no Pontual (RN-701), para um cliente da carteira.
 *
 * É a mesma conta da mesa de negociação — `POST /pontual/simulacoes`, feita no
 * servidor — sem o que é da operação: gravar a negociação, escolher o
 * fornecedor, enviar e-mail. O que o assessor faz com o resultado é **pedir o
 * fechamento**: o pedido leva os números como ele os viu, e a operação monta a
 * negociação com eles.
 */
export function SimulacaoParaOCliente(props: {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const empresa = useEmpresaCorrente();
  const queryClient = useQueryClient();
  const carteira = useCarteiraDoAssessor(props.aberto);

  const [clienteId, setClienteId] = useState('');
  const [valorFace, setValorFace] = useState('');
  const [desagioCompra, setDesagioCompra] = useState('20');
  const [desagioVenda, setDesagioVenda] = useState('12');
  const [parcelas, setParcelas] = useState('1');
  const [observacao, setObservacao] = useState('');

  const face = lerValorDigitado(valorFace) ?? 0;
  const compra = Number(desagioCompra.replace(',', '.'));
  const venda = Number(desagioVenda.replace(',', '.'));
  const vezes = Math.max(1, Math.trunc(Number(parcelas) || 1));
  const podeSimular =
    face > 0 &&
    Number.isFinite(compra) &&
    Number.isFinite(venda) &&
    compra >= 0 &&
    compra <= 100 &&
    venda >= 0 &&
    venda <= compra;

  const entradas = {
    valorFace: face,
    desagioCompraPercentual: compra,
    desagioVendaPercentual: venda,
    parcelas: vezes,
  };

  const simulacao = useQuery({
    queryKey: ['pontual-simulacao', face, compra, venda, vezes],
    queryFn: () =>
      requisicaoAutenticada<SimulacaoPontual>('/pontual/simulacoes', {
        metodo: 'POST',
        corpo: entradas,
      }),
    enabled: props.aberto && podeSimular,
  });
  const resultado = podeSimular ? simulacao.data : undefined;

  const pedir = useMutation({
    mutationFn: () =>
      pedirFechamento({
        empresa: empresa ?? '',
        servico: 'PONTUAL',
        clienteId,
        simulacao: { entradas, resultado },
        ...(observacao.trim() === '' ? {} : { observacao: observacao.trim() }),
      }),
    onSuccess: () => invalidarPedidos(queryClient),
  });

  // Reabrir começa do zero: o pedido anterior não fica na tela.
  useEffect(() => {
    if (!props.aberto) {
      pedir.reset();
      setValorFace('');
      setObservacao('');
    }
    // `pedir` muda a cada render; o que importa é abrir e fechar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.aberto]);

  const clientes = carteira.data ?? [];
  const semCarteira = carteira.isSuccess && clientes.length === 0;

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Simular para um cliente"
      descricao="Valor de face e os dois deságios. A conta é a mesma da operação; com o cliente de acordo, peça o fechamento."
      largura="larga"
      rodape={
        pedir.isSuccess ? (
          <Botao onClick={props.aoFechar}>Fechar</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={props.aoFechar}>
              Cancelar
            </Botao>
            <Botao
              disabled={clienteId === '' || resultado === undefined}
              carregando={pedir.isPending}
              onClick={() => pedir.mutate()}
            >
              Pedir fechamento
            </Botao>
          </>
        )
      }
    >
      {pedir.isSuccess ? (
        <Aviso tom="positivo" titulo="Pedido enviado à operação">
          A operação foi avisada e monta a negociação com estes números. O pedido aparece em “Meus
          pedidos de fechamento” até ser atendido.
        </Aviso>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="flex flex-col gap-4">
            <CampoDeSelecao
              rotulo="Cliente"
              ajuda="Da sua carteira — os clientes que a operação vinculou à sua assessoria."
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              disabled={semCarteira}
              opcoes={[
                { valor: '', rotulo: carteira.isPending ? 'Carregando…' : 'Selecione…' },
                ...clientes.map((c) => ({ valor: c.businessId, rotulo: c.razaoSocial })),
              ]}
            />
            {semCarteira && (
              <Aviso tom="atencao" titulo="Nenhum cliente na sua carteira">
                Dá para simular, mas o pedido de fechamento é para cliente seu. Peça à operação para
                vincular o cliente à sua assessoria.
              </Aviso>
            )}
            {carteira.isError && (
              <ErroDaApi titulo="Não foi possível carregar a carteira" erro={carteira.error} />
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <CampoDeMoeda
                rotulo="Valor de face do crédito"
                value={valorFace}
                onChange={setValorFace}
              />
              <Campo
                rotulo="Parcelas"
                numerico
                ajuda="1 = à vista; acima disso, mensais."
                value={parcelas}
                onChange={(e) => setParcelas(e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Pelo lado de quem dá o deságio, como na tabela do Pontual. */}
              <Campo
                rotulo="Deságio de venda"
                numerico
                sufixo="%"
                ajuda="O desconto que o fornecedor dá."
                value={desagioCompra}
                onChange={(e) => setDesagioCompra(e.target.value)}
              />
              <Campo
                rotulo="Deságio de compra"
                numerico
                sufixo="%"
                ajuda="O desconto que o comprador recebe."
                value={desagioVenda}
                onChange={(e) => setDesagioVenda(e.target.value)}
              />
            </div>

            {venda > compra && (
              <Aviso tom="atencao" titulo="O deságio do comprador passou o do fornecedor">
                O comprador pagaria menos do que o fornecedor recebe — não sobraria honorário.
              </Aviso>
            )}

            <CampoDeTexto
              rotulo="Observação para a operação"
              rows={2}
              ajuda="Opcional: prazo combinado, quem assina, o que o cliente pediu."
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />

            {pedir.isError && <ErroDaApi titulo="Não foi possível pedir" erro={pedir.error} />}
          </div>

          <aside className="flex h-fit flex-col gap-3 rounded-painel border border-borda bg-superficie-elevada p-4">
            <h3 className="font-display text-sm font-semibold tracking-tight">A negociação</h3>
            {resultado === undefined ? (
              <p className="text-sm text-conteudo-sutil">
                Informe o valor de face e os dois deságios para ver os números.
              </p>
            ) : (
              <dl className="flex flex-col gap-3">
                <Item rotulo="Fornecedor recebe">
                  <ValorFiscal valor={resultado.valorCompra} />
                </Item>
                <Item rotulo="Comprador paga">
                  <ValorFiscal valor={resultado.valorVenda} />
                </Item>
                <Item rotulo="Spread (honorários)">
                  <span className="flex items-baseline gap-2">
                    <ValorFiscal valor={resultado.spread} variante="economia" />
                    <ValorPercentual percentual={resultado.spreadPercentual} tamanho="pequeno" />
                  </span>
                </Item>
                <Item rotulo="Economia do comprador">
                  <ValorFiscal valor={resultado.economiaDoComprador} variante="economia" />
                </Item>
                {vezes > 1 && (
                  <div className="border-t border-borda pt-3">
                    <p className="text-xs uppercase tracking-wide text-conteudo-sutil">
                      {vezes} parcelas
                    </p>
                    <p className="mt-1 text-sm text-conteudo-secundario">
                      Comprador{' '}
                      <ValorFiscal valor={resultado.parcelas[0]?.valor} tamanho="pequeno" /> ·
                      honorários{' '}
                      <ValorFiscal valor={resultado.honorarios[0]?.valor} tamanho="pequeno" />
                    </p>
                  </div>
                )}
              </dl>
            )}
            {simulacao.isError && (
              <ErroDaApi titulo="Não foi possível simular" erro={simulacao.error} />
            )}
          </aside>
        </div>
      )}
    </Modal>
  );
}
